import { useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { useUpdateBedarf } from '../../../api/bedarfe';
import { BedarfPrioritaet, BedarfPrioritaetConfig } from '../../../types/bedarf';

interface ChangePriorityModalProps {
  isOpen: boolean;
  onClose: () => void;
  bedarfId: string;
  currentPriority: BedarfPrioritaet;
  childName: string;
  onSuccess?: () => void;
}

const PRIORITY_OPTIONS: { value: BedarfPrioritaet; label: string }[] = [
  { value: BedarfPrioritaet.NIEDRIG, label: 'Niedrig' },
  { value: BedarfPrioritaet.MITTEL, label: 'Mittel' },
  { value: BedarfPrioritaet.HOCH, label: 'Hoch' },
];

export function ChangePriorityModal({
  isOpen,
  onClose,
  bedarfId,
  currentPriority,
  childName,
  onSuccess,
}: ChangePriorityModalProps) {
  const updateMutation = useUpdateBedarf();
  const [selectedPriority, setSelectedPriority] = useState<BedarfPrioritaet>(currentPriority);

  const handleSubmit = async () => {
    if (selectedPriority === currentPriority) {
      onClose();
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: bedarfId,
        data: { priority: selectedPriority },
      });
      toast.success('Prioritaet erfolgreich geaendert');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to update priority:', error);
      toast.error('Prioritaet konnte nicht geaendert werden');
    }
  };

  const handleClose = () => {
    setSelectedPriority(currentPriority);
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleClose}
        />

        {/* Modal */}
        <div className="relative inline-block bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-md sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                <svg className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2zm9-13.5V9" />
                </svg>
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Prioritaet aendern
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Waehlen Sie die neue Prioritaet fuer <span className="font-medium">{childName}</span>.
                </p>

                <div className="mt-4 space-y-2">
                  {PRIORITY_OPTIONS.map((option) => {
                    const config = BedarfPrioritaetConfig[option.value];
                    const isSelected = selectedPriority === option.value;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => setSelectedPriority(option.value)}
                        className={`
                          w-full flex items-center px-4 py-3 rounded-lg border-2 transition-all
                          ${isSelected
                            ? `${config.bgColor} border-primary-500 ring-2 ring-offset-2 ring-primary-500`
                            : 'border-gray-200 hover:border-gray-300 bg-white'
                          }
                        `}
                      >
                        <div className={`w-3 h-3 rounded-full mr-3 ${config.bgColor.replace('-100', '-500')}`} />
                        <span className={`font-medium ${isSelected ? config.color : 'text-gray-700'}`}>
                          {option.label}
                        </span>
                        {option.value === currentPriority && (
                          <span className="ml-auto text-xs text-gray-500">(aktuell)</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={updateMutation.isPending || selectedPriority === currentPriority}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Wird gespeichert...' : 'Speichern'}
            </button>
            <button
              type="button"
              onClick={handleClose}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
